import { VList } from 'roosterjs-editor-dom';
import { Indentation } from 'roosterjs-editor-types';
import { fromEvent, merge } from 'rxjs';
import { share, tap } from 'rxjs/operators';
import { hasElementsIntersectingRange } from '../../helpers/dom/element';
import {
  addBulletedListInRange,
  isCursorAtStartOfListItem,
  isListElement,
  isListItemElement,
  ListElement,
  ListOperationDelegate,
  ListType,
} from '../../helpers/dom/list';
import { findAncestor } from '../../helpers/dom/node';
import { fromMutationObserver } from '../../helpers/fromMutationObserver';
import { ignoreDuringCompositions } from '../../helpers/ignoreDuringCompositions';
import { isEmptyIterable } from '../../helpers/iterable';
import {
  changeListIndentInRange,
  createVListFromNode,
  findSingleListType,
  parseVLists,
  toggleListTypeInRange,
} from '../../helpers/lists/vListHelpers';
import { ElementSelection, ElementSelectionService } from '../dom/elementSelectionService';
import { ElementService } from '../dom/elementService';
import { ListService } from '../text/listService';
import { HtmlListServiceOptions } from './htmlListService';

export class HtmlListServiceV2 extends ElementService<HTMLElement> implements ListService {
  constructor(
    element: HTMLElement,
    selectionService: ElementSelectionService,
    options: HtmlListServiceOptions,
  ) {
    super(
      element,
      {
        effects$: merge(
          fromEvent<KeyboardEvent>(element, 'keydown').pipe(
            ignoreDuringCompositions(element),
            tap(event => this.onKeyDown(event)),
          ),
          fromMutationObserver(element, {
            subtree: true,
            childList: true,
          }).pipe(tap(() => this.onMutate())),
        ).pipe(share()),
      },
      selectionService,
    );

    this.selectionService = selectionService;
    this.listOperationDelegate = {
      decorateList: list => {
        if (options.newListClassName) {
          // eslint-disable-next-line no-param-reassign
          list.className = options.newListClassName;
        }
      },
    };
  }

  private readonly selectionService: ElementSelectionService;
  private readonly listOperationDelegate: ListOperationDelegate;
  private shouldMaybeMergeListsOnMutate = false;

  private onKeyDown(event: KeyboardEvent): void {
    if (event.defaultPrevented) {
      return;
    }

    const { key } = event;
    switch (key) {
      case 'Tab':
        if (event.shiftKey) {
          if (this.canOutdent) {
            event.preventDefault();
            this.outdent();
          }
        } else if (this.canIndent) {
          event.preventDefault();
          this.indent();
        }
        break;

      case 'Backspace':
        this.setShouldMaybeMergeOnMutate(event);
        this.onBackspace(event);
        break;

      case 'Delete':
        this.setShouldMaybeMergeOnMutate(event);
        break;

      default:
    }
  }

  private onMutate() {
    if (!this.shouldMaybeMergeListsOnMutate) {
      return;
    }

    this.shouldMaybeMergeListsOnMutate = false;

    this.withSelection(({ range }) => {
      const parentList = findAncestor(range.startContainer, isListElement, this.element);
      if (!parentList) {
        return;
      }

      const next = parentList.nextElementSibling;
      if (isListElement(next) && next.tagName === parentList.tagName) {
        this.selectionService.withMutations(() => this.mergeLists(parentList, next, range));
      }

      const previous = parentList.previousElementSibling;
      if (isListElement(previous) && previous.tagName === parentList.tagName) {
        this.selectionService.withMutations(() => this.mergeLists(previous, parentList, range));
      }
    });
  }

  private mergeLists(previous: ListElement, next: ListElement, range: Range) {
    const vList = createVListFromNode(previous, this.element, range);
    if (vList) {
      vList.mergeVList(new VList(next));
      vList.writeBack();
    }
  }

  private setShouldMaybeMergeOnMutate(event: KeyboardEvent): void {
    // If the user pressed backspace or delete, and we're not inside of a list
    // item, we may have to merge lists after the resulting DOM mutation. Store
    // this state now to avoid having to re-check these conditions in the
    // mutation observer callback.
    if (
      (event.key === 'Delete' || event.key === 'Backspace') &&
      this.withSelection(({ range }) => !findAncestor(range.startContainer, isListItemElement, this.element))
    ) {
      this.shouldMaybeMergeListsOnMutate = true;
    }
  }

  private withSelection<T>(callback: (selection: ElementSelection) => T) {
    const { selection, extraSelectedElements } = this.selectionService;

    // We don't want to enable list creation across non-contiguous elements.
    if (!selection || !isEmptyIterable(extraSelectedElements)) {
      return undefined;
    }

    return callback(selection);
  }

  setType(newType: ListType | undefined) {
    this.withSelection(({ range, isBackward }) => {
      // Remove an existing list by outdenting selected items.
      if (!newType) {
        this.outdent();
        return;
      }

      const lists = parseVLists(range, this.element);

      // Add a list by indenting selected items/paragraphs/etc.
      if (newType && lists.length === 0) {
        // TODO: improve this parsing to handle multiple levels of indentation correctly. Might mean
        // re-writing it to use a Vlist.
        const newRange = addBulletedListInRange(this.element, range, newType, this.listOperationDelegate);
        this.selectionService.selectWithRange(newRange, isBackward);
        return;
      }

      // Change the type of a selected list:
      this.selectionService.withMutations(() => {
        lists.forEach(list => toggleListTypeInRange(list, range, newType));
      });
    });
  }

  get type() {
    return this.withSelection(({ range }) => findSingleListType(this.element, range));
  }

  get canIndent() {
    return this.getCanChangeIndentLevel() || false;
  }

  get canOutdent() {
    return this.getCanChangeIndentLevel() || false;
  }

  private getCanChangeIndentLevel() {
    return this.withSelection(({ range }) => {
      // Check if there are any list items.
      if (!hasElementsIntersectingRange(this.element, range, isListItemElement)) {
        return false;
      }

      // If the selection is collapsed, return true only if the cursor is at the beginning of a list item.
      if (range.collapsed) {
        const item = findAncestor(range.commonAncestorContainer, isListItemElement, this.element);
        return !!item && isCursorAtStartOfListItem(this.element, range, item);
      }

      // This is a selection that intersects a list, so we can indent.
      return true;
    });
  }

  indent() {
    this.changeIndentation(Indentation.Increase);
  }

  outdent() {
    this.changeIndentation(Indentation.Decrease);
  }

  private changeIndentation(indentation: Indentation) {
    this.withSelection(({ range }) => {
      this.selectionService.withMutations(() => {
        const lists = parseVLists(range, this.element);
        lists.forEach(list => changeListIndentInRange(list, range, indentation));
      });
    });
  }

  onBackspace(event: KeyboardEvent) {
    this.withSelection(({ range }) => {
      const listItem = findAncestor(range.startContainer, isListItemElement, this.element);
      if (!listItem) {
        return;
      }

      // We only want to handle delete if the cursor is collapsed at the start of a list item.
      if (!isCursorAtStartOfListItem(this.element, range, listItem)) {
        return;
      }

      const list = createVListFromNode(listItem, this.element, range);
      if (!list) {
        return;
      }

      // We got this.
      event.preventDefault();

      this.selectionService.withMutations(() => {
        if (listItem.previousElementSibling) {
          // This item is within a list - soft indent.
          changeListIndentInRange(list, range, Indentation.Decrease, true);
        } else {
          // This is the first item in the list, outdent to remove it.
          this.outdent();
        }
      });
    });
  }
}
